// ============================================================
// frontend/src/lib/useSaving.ts
// ============================================================
// THE most important hook in this app. Manages save button state.
//
// SOLVES THREE PROBLEMS:
// 1. Double-submit: while saving, the button is disabled.
// 2. Anxiety: spinner + "Saving…" shows the user something is happening.
// 3. Confirmation: brief "Saved ✓" flash after success.
//
// USAGE:
//   const { saving, saved, error, run } = useSaving();
//
//   const onSave = () => run(async () => {
//     await api.post('/clients', data);
//   });
//
//   <SaveButton saving={saving} saved={saved} onClick={onSave}>
//     Save
//   </SaveButton>
// ============================================================

import { useState, useRef, useCallback, useEffect } from 'react';

interface UseSavingOptions {
  /** How long the "Saved" indicator stays visible after success. */
  savedDurationMs?: number;
}

interface UseSavingReturn {
  saving: boolean;
  saved: boolean;
  error: Error | null;
  /** Run an async function while showing saving state. Returns its result, or null if a save is already in progress. */
  run: <T>(fn: () => Promise<T>) => Promise<T | null>;
  /** Manually clear the error state (e.g., when the user starts typing again). */
  clearError: () => void;
}

export function useSaving({ savedDurationMs = 1500 }: UseSavingOptions = {}): UseSavingReturn {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track mounted state so we don't call setState after unmount.
  // React would log a warning, and there's a minor memory leak risk.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // Track the "saved" reset timer so we can cancel it if a new save starts.
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    // CRITICAL: short-circuit if already saving. This is the primary
    // defense against double-submit. Button disabling is the secondary defense.
    if (saving) return null;

    // Cancel any pending "saved" indicator reset from a previous save.
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);

    setSaving(true);
    setSaved(false);
    setError(null);

    try {
      const result = await fn();
      if (!mountedRef.current) return result;

      setSaving(false);
      setSaved(true);

      savedTimerRef.current = setTimeout(() => {
        if (mountedRef.current) setSaved(false);
      }, savedDurationMs);

      return result;
    } catch (err) {
      if (!mountedRef.current) return null;
      setSaving(false);
      setSaved(false);
      setError(err instanceof Error ? err : new Error(String(err)));
      // Re-throw so the caller can also handle it (e.g., show a toast).
      throw err;
    }
  }, [saving, savedDurationMs]);

  const clearError = useCallback(() => setError(null), []);

  return { saving, saved, error, run, clearError };
}
